import React, { useState, useCallback } from 'react';
import {
  Box, Button, Tooltip, IconButton,
  Paper, MenuItem, Typography, Select, FormControl, Grid,
  Card, CardContent, CardHeader, InputAdornment, TextField
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import TagsDropdown from './TagsDropdown'; // Assuming TagsDropdown is in the same directory

import { useTranslation } from 'react-i18next';

const ConditionsEditor = ({
  useConditions,
  onUseConditionsChange,
  conditions,
  onConditionsChange,
  tags, // System and custom tags
  customDataTags, // Tags like {fileContentToWrite}
}) => {
  const [conditionTagAnchorEl, setConditionTagAnchorEl] = useState(null);
  const [addingConditionIndex, setAddingConditionIndex] = useState(null);
  const { t } = useTranslation();
  const handleAddCondition = useCallback(() => {
    onConditionsChange([...conditions, { tag: '', condition: 'equals', value: '' }]);
    // Always ensure useConditions is true when adding a condition
    if (!useConditions) {
      onUseConditionsChange(true);
    }
  }, [conditions, useConditions, onConditionsChange, onUseConditionsChange]);

  const handleRemoveCondition = useCallback((index) => {
    const newConditions = conditions.filter((_, i) => i !== index);
    onConditionsChange(newConditions);
  }, [conditions, onConditionsChange]);

  const handleConditionChange = useCallback((index, field, value) => {
    const newConditions = conditions.map((cond, i) =>
      i === index ? { ...cond, [field]: value } : cond
    );
    onConditionsChange(newConditions);
  }, [conditions, onConditionsChange]);

  const handleConditionTagClick = useCallback((event, index) => {
    setConditionTagAnchorEl(event.currentTarget);
    setAddingConditionIndex(index);
  }, []);
  const handleConditionTagClose = useCallback(() => {
    setConditionTagAnchorEl(null);
    // setAddingConditionIndex(null); // Keep index until a tag is selected or dropdown is dismissed
  }, []);

  const handleConditionTagSelect = useCallback((tagValue) => {
    if (addingConditionIndex !== null) {
      const newConditions = [...conditions];
      // Append the tag to the existing value or set it if empty
      // newConditions[addingConditionIndex].tag = newConditions[addingConditionIndex].tag ? `${newConditions[addingConditionIndex].tag} {${tagValue}}` : `{${tagValue}}`;
      // Or, if the button is for selecting the primary tag key for the condition:
      newConditions[addingConditionIndex].tag = tagValue;
      onConditionsChange(newConditions);
      // Always ensure useConditions is true when adding condition content
      if (!useConditions) {
        onUseConditionsChange(true);
      }
    }
    setConditionTagAnchorEl(null); // Close dropdown after selection
    setAddingConditionIndex(null); // Reset index
  }, [addingConditionIndex, conditions, onConditionsChange, useConditions, onUseConditionsChange]);

  const defaultTitle = t('conditionsEditor.when_label', 'WHEN');
  const actionStatusOptions = [
    { value: 'never_run', label: t('conditionsEditor.action_status_never_run', 'Never run') },
    { value: 'success', label: t('conditionsEditor.action_status_success', 'Success') },
    { value: 'failed', label: t('conditionsEditor.action_status_failed', 'Failed') },
    { value: 'skipped', label: t('conditionsEditor.action_status_skipped', 'Skipped') },
    { value: 'unknown', label: t('conditionsEditor.action_status_unknown', 'Unknown') },
    { value: 'unexpected', label: t('conditionsEditor.action_status_unexpected', 'Unexpected') },
  ];
  const booleanTagOptions = [
    { value: '1', label: t('conditionsEditor.boolean_true', 'True') },
    { value: '0', label: t('conditionsEditor.boolean_false', 'False') },
  ];

  return (
    <Card sx={{
      mb: 2,
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
      border: '1px solid #5757b010',
      overflow: 'hidden',
    }}>
      <CardHeader
        title={
          <Typography sx={{
            fontWeight: 700,
            color: '#444',
            fontSize: '1.1rem',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Box component="span" sx={{
              bgcolor: '#5757b0',
              color: 'white',
              borderRadius: '6px',
              px: 1.5,
              py: 0.7,
              mr: 2,
              fontSize: '0.85rem',
              letterSpacing: '0.5px',
              fontWeight: 600,
              boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
            }}>
              {defaultTitle}
            </Box>
            {t('conditionsEditor.when_to_process', 'Define conditions for this action to run')}
          </Typography>
        }
        sx={{
          borderBottom: '1px solid #f0f0f8',
          backgroundColor: '#fafbff',
          p: 2,
        }}
      />
      <CardContent sx={{ p: 2 }}>
        <Typography sx={{
          fontWeight: 500,
          color: '#444',
          fontSize: '0.95rem',
          backgroundColor: '#f3f4fb',
          p: 1.5,
          borderRadius: '10px',
          mb: 2,
          textAlign: 'center'
        }}>
          {conditions.length === 0
            ? t('conditionsEditor.emptyConditionsDescription')
            : t('conditionsEditor.filledConditionsDescription')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>          {conditions.length > 0 && (
            <Box sx={{ width: '100%', mb: 2 }}>
              {conditions.map((condition, index) => (
                <Box
                  key={`condition-${index}`}
                  sx={{
                    mb: 1,
                    p: 1.5,
                    border: '1px solid #e0e0f8',
                    borderRadius: '10px',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 1.5, // Increased gap
                    backgroundColor: '#fafbff',
                    '&:hover': {
                      backgroundColor: '#f3f5ff',
                      boxShadow: '0 4px 12px rgba(87, 87, 176, 0.05)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={(e) => handleConditionTagClick(e, index)}
                    sx={{
                      borderColor: condition.tag ? '#5757b040' : '#d0d0e0',
                      color: condition.tag ? '#5757b0' : '#777',
                      minWidth: { xs: '100%', sm: '150px' }, // Adjusted minWidth
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#5757b0',
                        backgroundColor: '#f0f0ff',
                      },
                      transition: 'all 0.2s ease',
                      py: 1,
                      textTransform: 'none',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block',
                      textAlign: 'left', // Ensure text aligns left
                      justifyContent: 'flex-start', // Ensure content aligns left
                    }}
                  >
                    {condition.tag || t('auto_create_file.select_tag', 'Select Tag')}
                  </Button>

                  <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                    <Select
                      value={condition.condition}
                      onChange={(e) => handleConditionChange(index, 'condition', e.target.value)}
                      sx={{
                        '.MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0e8' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#5757b0' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#5757b0', borderWidth: '1px' },
                        boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                        fontWeight: 500,
                      }}
                    >
                      <MenuItem value="equals">{t('conditionsEditor.condition_equals', 'Equals')}</MenuItem>
                      <MenuItem value="not_equals">{t('conditionsEditor.condition_not_equals', 'Not Equals')}</MenuItem>
                      <MenuItem value="contains">{t('conditionsEditor.condition_contains', 'Contains')}</MenuItem>
                      <MenuItem value="not_contains">{t('conditionsEditor.condition_not_contains', 'Not Contains')}</MenuItem>
                      <MenuItem value="startswith">{t('conditionsEditor.condition_startswith', 'Starts With')}</MenuItem>
                      <MenuItem value="endswith">{t('conditionsEditor.condition_endswith', 'Ends With')}</MenuItem>
                      <MenuItem value="match_regex">{t('conditionsEditor.condition_match_regex', 'Match Regex')}</MenuItem>
                      <MenuItem value="not_empty">{t('conditionsEditor.condition_not_empty', 'Is Not Empty')}</MenuItem>
                      <MenuItem value="is_empty">{t('conditionsEditor.condition_is_empty', 'Is Empty')}</MenuItem>
                      <MenuItem value="more">{t('conditionsEditor.condition_more', 'Greater Than (Numeric)')}</MenuItem>
                      <MenuItem value="less">{t('conditionsEditor.condition_less', 'Less Than (Numeric)')}</MenuItem>
                      <MenuItem value="file_exists">{t('conditionsEditor.condition_file_exists', 'File Exists')}</MenuItem>
                      <MenuItem value="folder_exists">{t('conditionsEditor.condition_folder_exists', 'Folder Exists')}</MenuItem>
                      {/* Add other operators as needed */}
                    </Select>
                  </FormControl>

                  {condition.condition !== 'not_empty' && condition.condition !== 'is_empty' && (
                    condition.tag === 'action_last_status' ? (
                      <>
                        <FormControl size="small" sx={{ flex: 1, minWidth: { xs: '100%', sm: 180 } }}>
                          <Select
                            value={actionStatusOptions.some((opt) => opt.value === condition.value) ? condition.value : '__custom__'}
                            onChange={(e) => {
                              const nextValue = e.target.value === '__custom__' ? '' : e.target.value;
                              handleConditionChange(index, 'value', nextValue);
                            }}
                            sx={{
                              '.MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0e8' },
                              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#5757b0' },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#5757b0', borderWidth: '1px' },
                              boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                              fontWeight: 500,
                            }}
                          >
                            {actionStatusOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                            <MenuItem value="__custom__">
                              {t('conditionsEditor.action_status_custom', 'Custom')}
                            </MenuItem>
                          </Select>
                        </FormControl>
                        {!actionStatusOptions.some((opt) => opt.value === condition.value) && (
                          <TextField
                            value={condition.value}
                            onChange={(e) => handleConditionChange(index, 'value', e.target.value)}
                            size="small"
                            placeholder={t('conditionsEditor.condition_value_placeholder', 'Value')}
                            sx={{
                              flex: 1,
                              width: { xs: '100%', sm: 'auto' },
                              '.MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0e8' },
                              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#5757b0' },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#5757b0', borderWidth: '1px' },
                              boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                            }}
                          />
                        )}
                      </>
                    ) : (condition.tag === 'isedited' || condition.tag === 'confirmed') ? (
                      <FormControl size="small" sx={{ flex: 1, minWidth: { xs: '100%', sm: 180 } }}>
                        <Select
                          value={condition.value === '1' || condition.value === '0' ? condition.value : '1'}
                          onChange={(e) => handleConditionChange(index, 'value', e.target.value)}
                          sx={{
                            '.MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0e8' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#5757b0' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#5757b0', borderWidth: '1px' },
                            boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                            fontWeight: 500,
                          }}
                        >
                          {booleanTagOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        value={condition.value}
                        onChange={(e) => handleConditionChange(index, 'value', e.target.value)}
                        size="small"
                        placeholder={t('conditionsEditor.condition_value_placeholder', 'Value')}
                        sx={{
                          flex: 1,
                          width: { xs: '100%', sm: 'auto' },
                          '.MuiOutlinedInput-notchedOutline': { borderColor: '#d0d0e8' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#5757b0' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#5757b0', borderWidth: '1px' },
                          boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                        }}
                      />
                    )
                  )}

                  <IconButton
                    onClick={() => handleRemoveCondition(index)}
                    size="medium"
                    sx={{
                      color: '#ff5252',
                      backgroundColor: 'rgba(255,82,82,0.08)',
                      '&:hover': {
                        backgroundColor: 'rgba(255,82,82,0.15)',
                      },
                      transition: 'all 0.2s ease',
                      alignSelf: { xs: 'flex-end', sm: 'center' },
                      ml: { xs: 'auto', sm: 0 },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          <Button
            startIcon={<AddIcon />}
            onClick={handleAddCondition}
            variant="outlined"
            size="medium"
            sx={{
              borderColor: '#5757b0',
              color: '#5757b0',
              '&:hover': {
                borderColor: '#4a4a90',
                backgroundColor: '#f0f0ff',
                boxShadow: '0 4px 12px rgba(87, 87, 176, 0.1)',
              },
              transition: 'all 0.2s ease',
              px: 3,
              py: 1,
              borderRadius: '8px',
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            {t('conditionsEditor.add_condition', 'Add Condition')}
          </Button>
        </Box>
      </CardContent>
      <TagsDropdown
        tags={tags}
        additionalTags={customDataTags} // Pass any additional specific tags if needed
        handleTagSelect={handleConditionTagSelect}
        anchorEl={conditionTagAnchorEl}
        setAnchorEl={handleConditionTagClose}
      />
    </Card>
  );
};

export default React.memo(ConditionsEditor);
